import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api.js';
import { Layout } from '../components/Layout.js';
import { IngredientChips } from '../components/IngredientChips.js';
import { Icon } from '../components/ui/Icon.js';

interface InstructionStep {
  id: string;
  order: number;
  text: string;
  ingredients: {
    recipeIngredientId: string;
    ingredientName: string;
    quantityText: string | null;
    unitText: string | null;
    rawText: string;
  }[];
}

interface RecipeDetailData {
  id: string;
  name: string;
  mealType: string;
  cuisine: string | null;
  instructions: InstructionStep[];
  cookingMethod: string[] | null;
  cookTimeMins: number | null;
  difficulty: string | null;
  tags: string[] | null;
  ingredients: {
    rawText: string;
    quantityText: string | null;
    unitText: string | null;
    ingredientName: string;
  }[];
}

const MEAL_TYPE_COLOR: Record<string, string> = {
  breakfast: 'bg-amber-50 text-amber-700',
  lunch: 'bg-blue-50 text-blue-700',
  dinner: 'bg-purple-50 text-purple-700',
};

function RecipeImage({ name }: { name: string }) {
  const { data } = useQuery<{ imageUrl: string | null }>({
    queryKey: ['recipe-image', name],
    queryFn: () => api.get(`/api/recipes/image?name=${encodeURIComponent(name)}`),
    staleTime: Infinity,
    retry: false,
  });

  if (!data) {
    return <div className="w-full h-48 bg-gray-100 dark:bg-gray-800 animate-pulse rounded-xl mb-6" />;
  }

  if (!data.imageUrl) return null;

  return (
    <img
      src={data.imageUrl}
      alt={name}
      className="w-full object-cover rounded-xl mb-6"
      style={{ maxHeight: '220px' }}
      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
    />
  );
}

interface FavoriteEntry { id: string; recipeId: string; }

function HeartButton({ recipeId, recipeName, mealType }: { recipeId: string; recipeName: string; mealType: string }) {
  const qc = useQueryClient();
  const navigate = useNavigate();

  const { data: favorites = [] } = useQuery<FavoriteEntry[]>({
    queryKey: ['favorites'],
    queryFn: () => api.get('/api/favorites'),
    staleTime: 5 * 60_000,
  });

  const isFav = favorites.some((f) => f.recipeId === recipeId);

  const remove = useMutation({
    mutationFn: () => api.delete(`/api/favorites/${recipeId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['favorites'] });
      navigate('/favorites');
    },
  });

  const add = useMutation({
    mutationFn: () => api.post('/api/favorites', { recipeId, recipeName, mealType }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['favorites'] }),
  });

  return (
    <button
      onClick={() => isFav ? remove.mutate() : add.mutate()}
      disabled={add.isPending || remove.isPending}
      aria-label={isFav ? 'Remove from favorites' : 'Add to favorites'}
      className="text-2xl leading-none disabled:opacity-50 transition-colors"
      style={{ color: isFav ? '#ef4444' : undefined }}
    >
      {isFav ? '♥' : '♡'}
    </button>
  );
}

export function RecipeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const { data: recipe, isLoading, isError } = useQuery<RecipeDetailData>({
    queryKey: ['recipe', id],
    queryFn: () => api.get(`/api/recipes/${id}`),
    enabled: !!id,
    retry: false,
  });

  return (
    <Layout>
      <div className="mb-4">
        <Link
          to="/favorites"
          className="text-sm font-medium hover:underline"
          style={{ color: 'var(--accent)' }}
        >
          ← Back to favorites
        </Link>
      </div>

      {isLoading ? (
        <div className="space-y-4 animate-pulse">
          <div className="h-48 bg-gray-200 dark:bg-gray-700 rounded-xl" />
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-2/3" />
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
          <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded" />
        </div>
      ) : isError || !recipe ? (
        <p className="text-sm text-red-500">Recipe not found.</p>
      ) : (
        <div className="card overflow-hidden">
          <RecipeImage name={recipe.name} />

          <div className="px-6 pb-6">
            {/* Title row */}
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${MEAL_TYPE_COLOR[recipe.mealType] ?? 'bg-gray-100 text-gray-600'}`}>
                    {recipe.mealType}
                  </span>
                  {recipe.cuisine && (
                    <span className="text-xs text-gray-400">{recipe.cuisine}</span>
                  )}
                </div>
                <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {recipe.name}
                </h1>
              </div>
              <HeartButton
                recipeId={recipe.id}
                recipeName={recipe.name}
                mealType={recipe.mealType}
              />
            </div>

            {/* Engagement badges */}
            {(recipe.cookTimeMins || recipe.difficulty) && (
              <div className="flex items-center gap-3 mb-5">
                {recipe.cookTimeMins && (
                  <span className="inline-flex items-center gap-1 text-xs text-gray-500 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10" strokeWidth="2"/>
                      <path strokeLinecap="round" d="M12 6v6l4 2" strokeWidth="2"/>
                    </svg>
                    {recipe.cookTimeMins} min
                  </span>
                )}
                {recipe.difficulty && (
                  <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                    {(['easy','intermediate','expert'] as const).map((level) => (
                      <span
                        key={level}
                        className={`w-2 h-2 rounded-full ${
                          recipe.difficulty === 'easy' && level === 'easy'
                            ? 'bg-emerald-500'
                            : recipe.difficulty === 'intermediate' && (level === 'easy' || level === 'intermediate')
                            ? 'bg-amber-400'
                            : recipe.difficulty === 'expert'
                            ? 'bg-red-500'
                            : 'bg-gray-200'
                        }`}
                      />
                    ))}
                    <span className="ml-0.5 capitalize">{recipe.difficulty}</span>
                  </span>
                )}
              </div>
            )}

            {/* Start cooking CTA */}
            <button
              onClick={() => navigate(`/recipes/${recipe.id}/cook/0`, {
                state: { returnTo: location.pathname },
              })}
              disabled={recipe.instructions.length === 0}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 font-semibold text-sm mb-5 disabled:opacity-50"
              style={{
                background: 'var(--accent)',
                color: 'var(--bg-deep)',
                boxShadow: '0 6px 18px -6px var(--accent-glow)',
              }}
            >
              <Icon name="cookpot" size={16} stroke={2.2} />
              Start cooking
            </button>

            {/* Ingredients */}
            <div className="mb-5">
              <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Ingredients</h2>
              <ul className="flex flex-wrap gap-2">
                {recipe.ingredients.map((ing, i) => (
                  <li
                    key={i}
                    className="text-xs text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded"
                  >
                    {ing.rawText}
                  </li>
                ))}
              </ul>
            </div>

            {/* Instructions */}
            <div>
              <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Instructions</h2>
              <ol className="space-y-3">
                {recipe.instructions.map((step, i) => (
                  <li key={step.id} className="flex gap-3">
                    <span className="text-sm font-semibold text-gray-400 dark:text-gray-500 flex-shrink-0 w-6">
                      {i + 1}.
                    </span>
                    <div className="min-w-0 flex-1 text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                      <p>{step.text}</p>
                      <IngredientChips ingredients={step.ingredients} />
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            {/* Tags */}
            {recipe.tags && recipe.tags.length > 0 && (
              <div className="mt-5 flex flex-wrap gap-1.5">
                {recipe.tags.map((tag) => (
                  <span key={tag} className="text-xs text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
}
